// Template list screen — all Templates with create / duplicate / delete
// actions. Per spec/build-plan.md P-024 row + tech-arch.md §6.
//
// **Confirm modal for delete; non-destructive duplicate fires inline.**
// Per the row QA item ("Destructive actions show a confirm dialog;
// non-destructive don't"). The `useDeleteConfirm` hook owns the modal
// state machine; this screen wires the hook to a `<ConfirmModal>` and
// the rendered list rows.
//
// **JSON download + load dropped at P-037.31 §C.** V1's archival/restore
// mechanism (P-034 / D-096) is superseded by V2's planned cloud sync.
// Templates have no surviving export path — they're cloned via
// `Duplicate` and snapshotted into Emails at create-from-Template time.

import { sortByUpdatedDesc } from '../../lib/sort'
import { createAndOpenTemplate, duplicateTemplate } from '../../store/factories'
import type { Template } from '../../store/schemas'
import { store } from '../../store/store'
import { ConfirmModal } from '../confirm-modal'
import { TEMPLATE_NAME_PLACEHOLDER } from '../constants'
import { useDeleteConfirm } from '../hooks/use-delete-confirm'
import { useStore } from '../hooks/use-store'
import { RecordRow } from './record-row'

function handleDuplicate(t: Template) {
  const dup = duplicateTemplate(t)
  store.setState((s) => ({ ...s, templates: [...s.templates, dup] }))
}

function handleDelete(id: string) {
  store.setState((s) => ({
    ...s,
    templates: s.templates.filter((t) => t.id !== id),
    currentTemplateId: s.currentTemplateId === id ? null : s.currentTemplateId,
  }))
}

export function TemplateListScreen() {
  const { templates } = useStore()
  const sorted = sortByUpdatedDesc(templates)
  const { pending, request, onConfirm, onCancel } = useDeleteConfirm(templates, handleDelete)

  return (
    <div class="space-y-6">
      <header class="flex flex-wrap items-baseline justify-between gap-2">
        <h2 class="m-0 text-lg font-semibold">Templates</h2>
        <div class="flex flex-wrap items-baseline gap-2">
          <button
            type="button"
            onClick={createAndOpenTemplate}
            class="rounded bg-ypo-navy px-3 py-1.5 text-sm font-medium text-white hover:bg-ypo-navy-deep"
          >
            New Template
          </button>
        </div>
      </header>

      {sorted.length === 0 ? (
        <p class="m-0 text-sm text-gray-600">
          No Templates yet. Click "New Template" to create your first.
        </p>
      ) : (
        <ul class="m-0 list-none space-y-2 p-0">
          {sorted.map((t) => (
            <RecordRow
              key={t.id}
              name={t.name || TEMPLATE_NAME_PLACEHOLDER}
              subtitle={`${t.blocks.length} block${t.blocks.length === 1 ? '' : 's'}`}
              editHref={`#/templates/${t.id}`}
              onDuplicate={() => handleDuplicate(t)}
              onDelete={() => request(t.id)}
            />
          ))}
        </ul>
      )}

      <ConfirmModal
        open={pending !== undefined}
        title="Delete this Template?"
        message={
          pending !== undefined
            ? `"${pending.name || TEMPLATE_NAME_PLACEHOLDER}" will be removed. This cannot be undone.`
            : ''
        }
        onConfirm={onConfirm}
        onCancel={onCancel}
      />
    </div>
  )
}
